import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  makeStyles
} from "@material-ui/core";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2)
  }
}));

const HistoryChart = ({ loading, error, history, onFetchHistory }) => {
  const classes = useStyles();
  const [days, setDays] = useState(7);

  const handleDaysChange = (event) => {
    const newDays = event.target.value;
    setDays(newDays);
    if (onFetchHistory) {
      onFetchHistory(newDays);
    }
  };

  if (loading && history.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && history.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  const chartData = history.map((item) => ({
    date: item.date || (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : new Date().toLocaleDateString()),
    diskUsage: parseFloat(item.diskUsage || item.avgDiskUsage || 0),
    binaryLogsSize: parseFloat(item.binaryLogsSize || item.avgBinaryLogsSize || 0),
    memoryUsage: parseFloat(item.memoryUsage || item.avgMemoryUsage || 0),
    cpuUsage: parseFloat(item.cpuUsage || item.avgCpuUsage || 0)
  }));

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h6">Historial de Monitoreo</Typography>
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Período</InputLabel>
          <Select value={days} onChange={handleDaysChange} label="Período">
            <MenuItem value={7}>Últimos 7 días</MenuItem>
            <MenuItem value={14}>Últimos 14 días</MenuItem>
            <MenuItem value={30}>Últimos 30 días</MenuItem>
            <MenuItem value={60}>Últimos 60 días</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {history.length === 0 ? (
        <Paper className={classes.chartContainer}>
          <Typography variant="body1" align="center" color="textSecondary">
            No hay datos históricos disponibles
          </Typography>
        </Paper>
      ) : (
        <Paper className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" label={{ value: "Porcentaje (%)", angle: -90, position: "insideLeft" }} />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ value: "Tamaño (GB)", angle: 90, position: "insideRight" }}
              />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="diskUsage"
                stroke="#f44336"
                name="Uso de Disco (%)"
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="memoryUsage"
                stroke="#2196f3"
                name="Uso de Memoria (%)"
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="cpuUsage"
                stroke="#ff9800"
                name="Uso de CPU (%)"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="binaryLogsSize"
                stroke="#9c27b0"
                name="Binary Logs (GB)"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default HistoryChart;
